import React from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Drill, Truck, Factory } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const upstreamCalls = [
  "Improving Lost Circulation Management in Unconventional Wells",
  "Call for Lessons Learned: Kick Detection and Well Control Events", 
  "Optimizing Horizontal Wellbore Cleaning Strategies",
  "AI-Ready Framework for Real-Time Drilling Performance Metrics",
  "Knowledge Base on Enhanced Oil Recovery (EOR) Pilot Learnings",
  "Seismic-to-Reservoir Workflow Best Practices in Shale Plays",
  "Reducing Non-Productive Time (NPT) Through Workflow Standardization",
  "Fracture Stimulation Design Optimization Based on Field Experience",
  "Knowledge Transfer on Aging Well Re-Entry and Recompletion Techniques",
  "Developing a Safety Protocol KB for HPHT Drilling Environments"
];

const midstreamCalls = [
  "Leak Detection & Response Procedures for Aging Pipelines",
  "Best Practices for SCADA System Integration Across Assets",
  "Call for Insights: Pipeline Pigging Optimization Strategies",
  "Digital Twin Readiness: Pipeline Asset Tagging and Data Standards",
  "Reducing Downtime in Crude Oil Pumping Stations",
  "Knowledge Base on Regulatory Compliance in Cross-Border Transport",
  "Emergency Response Procedures for Tank Farms and Storage Facilities",
  "Smart Contract Templates for Vendor Logistics Coordination",
  "Carbon Emission Tracking Protocols in Midstream Operations",
  "Training Content for Field Techs on Valve Inspection and Repair"
];

const downstreamCalls = [
  "Turnaround Optimization: Knowledge Base on Shutdown/Startup Sequences",
  "Improving Process Safety in Fluid Catalytic Cracking (FCC) Units",
  "AI-Driven Yield Optimization for Multi-Product Refineries",
  "Best Practices for Hydrogen Blending in Natural Gas Streams",
  "Emission Reduction Protocols for Flares and Vents",
  "Maintenance Knowledge Base for High-Temperature Heat Exchangers",
  "Digital SOPs for Refinery Lab Testing and QA/QC",
  "Call for Solutions: AI-Based Root Cause Analysis for Refinery Incidents",
  "Retail Fuel Distribution Optimization in High-Demand Regions",
  "KB for Training Station Operators on EV and Traditional Fuel Coexistence"
];

const ChevronCallForSolutions = () => {
  const navigate = useNavigate();

  const handleCallClick = (callTitle: string, domain: string) => {
    // Navigate to the CfS flow with pre-populated Chevron-specific data
    navigate("/call-for-solutions/bounty", {
      state: {
        selectedArea: "Chevron Energy Operations",
        chevronCall: {
          title: callTitle,
          domain: domain,
          organization: "Chevron",
          bountyAmount: "10000", // CfS gets $10k
          bountyType: "Enterprise",
          contributors: ["Trey Fleming", "Karen Watson", "Pete Dumont", "Giblet"],
          description: generateDescription(callTitle, domain)
        }
      }
    });
  };

  const generateDescription = (title: string, domain: string) => {
    return `Chevron ${domain} Operations seeks innovative solutions for: ${title}. This initiative aims to leverage industry best practices and emerging technologies to enhance operational efficiency, safety, and environmental stewardship across our global operations.`;
  };
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <Link to="/chevron-channel">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Chevron Channel
            </Button>
          </Link>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Chevron Call for Solutions
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Demonstration calls for solutions across Chevron's operational domains
          </p>
        </div>

        {/* Upstream Section */}
        <Card className="mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Drill className="h-6 w-6" />
              Upstream - Exploration, Drilling, and Production
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {upstreamCalls.map((call, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-auto p-4 text-left justify-start hover:bg-primary/5 hover:text-foreground text-foreground"
                  onClick={() => handleCallClick(call, "Upstream")}
                >
                  <span className="text-sm font-medium text-foreground">{call}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Midstream Section */}
        <Card className="mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Truck className="h-6 w-6" />
              Midstream - Transportation, Storage, and Logistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {midstreamCalls.map((call, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-auto p-4 text-left justify-start hover:bg-primary/5 hover:text-foreground text-foreground"
                  onClick={() => handleCallClick(call, "Midstream")}
                >
                  <span className="text-sm font-medium text-foreground">{call}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Downstream Section */}
        <Card className="mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Factory className="h-6 w-6" />
              Downstream - Refining, Distribution, and Retail
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {downstreamCalls.map((call, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-auto p-4 text-left justify-start hover:bg-primary/5 hover:text-foreground text-foreground"
                  onClick={() => handleCallClick(call, "Downstream")}
                >
                  <span className="text-sm font-medium text-foreground">{call}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChevronCallForSolutions;